/**
 * Objective triage of an inbox item into `ready` | `check` | `needs_you`
 * (product spec section 7, "Pipeline" step 4). Pure: everything it needs is in
 * the input, the reasons are Swedish sentences shown as `tierReasons`.
 *
 * Order of precedence: any `needs_you` condition wins, then any `check`
 * condition, otherwise the item is `ready` when the positive conditions hold.
 * A receipt without a VAT breakdown never defaults to 25 %: it is `check`
 * with the VAT question (the pipeline clears `proposal.vat` for it).
 */
import { daysBetween } from '../bank/match';
import { ALWAYS_NEEDS_YOU_ORE } from '../company-settings/defaults';
import { isWithin } from '../fiscal-years/service';
import { formatSek } from '../money';
import type { Ore } from '../money';
import { firstOpenDate, formatSwedishDate } from '../periods/deadlines';
import { vatFromGross } from '../transactions/vat';
import type { InboxFlag, InboxTier, IsoDate, ProposalQuestion, ReceiptReading } from '../types';
import type { TriageInput, TriageResult } from './types';

export const READY_CONFIDENCE = 0.85;
export const CHECK_CONFIDENCE = 0.6;
/** ±1 kr tolerance when checking Σ vatLines against the total. */
export const VAT_TOLERANCE_ORE = 100;
/** "Same purchase" window for duplicate detection (days on either side). */
export const DUPLICATE_WINDOW_DAYS = 2;

/** Flags that alone make an item `check` (the user glances, then books). */
export const SOFT_FLAGS: ReadonlySet<InboxFlag> = new Set<InboxFlag>([
  'representation',
  'possible_private',
  'no_vat_breakdown',
  'foreign_supplier',
  'asset_candidate',
]);

/** Flags that alone make an item `needs_you`. */
export const HARD_FLAGS: ReadonlySet<InboxFlag> = new Set<InboxFlag>([
  'duplicate',
  'unreadable',
  'locked_period',
  'outside_fiscal_year',
  'large_amount',
]);

export const NO_VAT_BREAKDOWN_QUESTION: ProposalQuestion = {
  field: 'vat',
  text: 'Vilken moms gäller? Kvittot visar ingen momsrad.',
  options: [
    { label: '25 %', value: '25' },
    { label: '12 %', value: '12' },
    { label: '6 %', value: '6' },
    { label: '0 %', value: '0' },
  ],
};

export const TRIAGE_REASON = {
  unreadable: 'Kvittot går inte att läsa – fyll i uppgifterna själv.',
  notReceipt: 'Ser inte ut som ett kvitto eller en faktura.',
  missingTotal: 'Beloppet saknas.',
  missingDate: 'Datumet saknas.',
  missingCategory: 'Kategori saknas.',
  /** The proposal names a category the catalog does not know (no account to book on). */
  unknownCategory: 'Kategorin finns inte i katalogen – välj en annan.',
  lowConfidence: 'AI\u2011förslaget är osäkert.',
  mediumConfidence: 'AI\u2011förslag, osäker på kategori',
  duplicate: 'Ser ut som samma köp som redan finns.',
  duplicateItem: 'Ser ut som samma köp som ett annat kvitto i Att göra.',
  foreignCurrency: (currency: string, totalOre: Ore | null = null) =>
    totalOre !== null ? `Kvittot är på ${formatSek(totalOre)} ${currency}. Vad drogs från kontot i kronor?` : `Kvittot är i ${currency}. Vad drogs från kontot i kronor?`,
  foreignCurrencyMatched: (currency: string, amountOre: Ore) => `Kvittot är i ${currency} – ${formatSek(amountOre)} kr togs från banktransaktionen. Stämmer det?`,
  largeAmount: `Belopp över ${formatSek(ALWAYS_NEEDS_YOU_ORE)} kr – bokförs alltid av dig.`,
  aboveBatch: (thresholdOre: Ore) => `Belopp över ${formatSek(thresholdOre)} kr – tas inte med i gruppbokföring.`,
  outsideFiscalYear: 'Datumet ligger utanför ett öppet räkenskapsår.',
  lockedPeriod: (firstOpen: IsoDate, relativeTo: IsoDate) =>
    `Perioden är låst. Bokförs på första öppna dag: ${formatSwedishDate(firstOpen, { relativeTo })}`,
  noVatBreakdown: 'Kvittot visar ingen momsrad – välj momssats.',
  vatMismatch: 'Momsraderna stämmer inte med totalen.',
  question: 'En fråga att svara på.',
  categoryConflict: (remembered: string, read: string) => `Du brukar bokföra det här stället som ${remembered}, men kvittot ser ut som ${read}.`,
  softFlag: {
    representation: 'Representation – ange antal personer.',
    possible_private: 'Kan vara privat – kolla att det är företagets köp.',
    no_vat_breakdown: 'Kvittot visar ingen momsrad – välj momssats.',
    foreign_supplier: 'Utländsk säljare – omvänd moms kan gälla.',
    asset_candidate: 'Dyr utrustning – bokförs som inventarie och skrivs av.',
  } as Readonly<Partial<Record<InboxFlag, string>>>,
  memory: (merchant: string) => `Förifyllt från minnet för ${merchant}.`,
  match: (date: IsoDate, relativeTo: IsoDate) => `Matchad mot banktransaktion ${formatSwedishDate(date, { relativeTo })}.`,
  aiConfident: 'AI\u2011förslag, säker',
} as const;

/**
 * Do the VAT lines agree with the total (±1 kr per rate)? With bases known
 * the lines must add up to (at most) the total; with a single rate and no
 * base the printed VAT must match `vatFromGross(total, rate)`. A receipt with
 * no VAT lines is not "inconsistent" – that case is `no_vat_breakdown`.
 */
export function vatLinesMatchTotal(lines: ReceiptReading['vatLines'], totalOre: Ore): boolean {
  if (lines.length === 0) return true;
  const total = Math.abs(totalOre);
  const vatSum = lines.reduce((s, l) => s + l.amountOre, 0);
  if (vatSum > total) return false;
  if (lines.every((l) => l.baseOre !== null)) {
    const gross = lines.reduce((s, l) => s + (l.baseOre ?? 0) + l.amountOre, 0);
    return gross <= total + VAT_TOLERANCE_ORE;
  }
  if (lines.length === 1) {
    const line = lines[0]!;
    return Math.abs(vatFromGross(total, line.rate) - line.amountOre) <= VAT_TOLERANCE_ORE || line.amountOre < vatFromGross(total, line.rate);
  }
  // Several rates without bases: we can only check that VAT does not exceed
  // the highest possible share.
  const maxRate = Math.max(...lines.map((l) => l.rate));
  return vatSum <= vatFromGross(total, maxRate as 25 | 12 | 6 | 0) + VAT_TOLERANCE_ORE;
}

/** "Same merchant + same amount + date within ±2 days" – the duplicate rule. */
export function looksLikeDuplicate(
  a: { merchantKey: string; amountOre: Ore; date: IsoDate },
  b: { merchantKey: string; amountOre: Ore; date: IsoDate },
): boolean {
  return a.merchantKey !== '' && a.merchantKey === b.merchantKey && a.amountOre === b.amountOre && Math.abs(daysBetween(a.date, b.date)) <= DUPLICATE_WINDOW_DAYS;
}

function addUnique<T>(list: T[], value: T): void {
  if (!list.includes(value)) list.push(value);
}

/**
 * Tier, reasons and flags for one item. Never books, never changes the
 * proposal – the pipeline applies `NO_VAT_BREAKDOWN_QUESTION` when the
 * `no_vat_breakdown` flag comes back.
 */
export function triage(input: TriageInput): TriageResult {
  const { item, proposal, reading, settings } = input;
  const flags: InboxFlag[] = [...item.flags];
  const hard: string[] = [];
  const soft: string[] = [];
  const isReceipt = item.kind === 'receipt';
  const fromMemory = proposal.reason === 'memory' || proposal.reason === 'rule';
  const matched = item.matchedBankRowIds.length > 0;
  // The confidence thresholds are about AI suggestions; a rule, a match or the
  // user's own answer (reason null) is not "uncertain".
  const aiSuggested = proposal.reason === 'suggestion';
  // While a "what was this?" card is open the kind is undecided, so the
  // category is not required yet – the answer decides what is needed.
  const kindUndecided = proposal.question?.field === 'kind';
  const foreign = !!reading && !!reading.currency && reading.currency.toUpperCase() !== 'SEK';
  // A printed total in another currency is never the amount in kronor.
  const amount = proposal.amountOre ?? (foreign ? null : (reading?.totalOre ?? null));
  const date = proposal.date ?? reading?.date ?? null;

  // ---- needs_you ------------------------------------------------------------
  if (reading && !reading.readable) {
    addUnique(flags, 'unreadable');
    hard.push(TRIAGE_REASON.unreadable);
  }
  if (reading && reading.documentType === 'annat') hard.push(TRIAGE_REASON.notReceipt);
  // A foreign receipt without a match asks for the kronor amount below; saying "saknas" too would be noise.
  if ((amount === null || amount <= 0) && !(foreign && !matched)) hard.push(TRIAGE_REASON.missingTotal);
  if (date === null) hard.push(TRIAGE_REASON.missingDate);
  if (needsCategory(proposal.kind) && !kindUndecided) {
    if (!proposal.categoryId) hard.push(TRIAGE_REASON.missingCategory);
    // "Category maps to an active account or one activation": a category the
    // catalog cannot resolve has no account at all, so it cannot be booked.
    else if (input.targetAccount === undefined && input.activeAccounts.size > 0) hard.push(TRIAGE_REASON.unknownCategory);
  }
  if (aiSuggested && !matched && proposal.confidence < CHECK_CONFIDENCE) hard.push(TRIAGE_REASON.lowConfidence);
  if (input.duplicateOf || item.flags.includes('duplicate')) {
    addUnique(flags, 'duplicate');
    hard.push(TRIAGE_REASON.duplicate);
  }
  if (foreign) {
    addUnique(flags, 'foreign_currency');
    if (!matched) hard.push(TRIAGE_REASON.foreignCurrency(reading.currency.toUpperCase(), reading.totalOre));
  }
  if (amount !== null && amount > ALWAYS_NEEDS_YOU_ORE) {
    addUnique(flags, 'large_amount');
    hard.push(TRIAGE_REASON.largeAmount);
  }
  if (date !== null) {
    const inOpenYear = input.openFiscalYears.some((fy) => isWithin(date, fy));
    if (!inOpenYear) {
      addUnique(flags, 'outside_fiscal_year');
      hard.push(TRIAGE_REASON.outsideFiscalYear);
    } else if (input.lockedThrough !== undefined && date <= input.lockedThrough) {
      addUnique(flags, 'locked_period');
      hard.push(TRIAGE_REASON.lockedPeriod(firstOpenDate(input.lockedThrough), date));
    }
  }
  for (const flag of item.flags) {
    if (HARD_FLAGS.has(flag) && !hard.length) hard.push(TRIAGE_REASON.softFlag[flag] ?? 'Behöver din bedömning.');
  }

  // ---- check ----------------------------------------------------------------
  // No VAT breakdown: the pipeline clears the AI's rate (never a silent 25 %),
  // so an unanswered receipt has `vat === null`; once the user picks a rate
  // the question is answered and the flag goes away.
  const noVatBreakdown =
    isReceipt && !!reading && reading.vatLines.length === 0 && settings.vatRegistered && proposal.kind === 'purchase' && !fromMemory && proposal.vat === null;
  if (noVatBreakdown || (item.flags.includes('no_vat_breakdown') && proposal.vat === null)) {
    addUnique(flags, 'no_vat_breakdown');
  } else if (!noVatBreakdown) {
    const i = flags.indexOf('no_vat_breakdown');
    if (i >= 0) flags.splice(i, 1);
  }
  if (reading && amount !== null && !(foreign && matched) && !vatLinesMatchTotal(reading.vatLines, amount)) soft.push(TRIAGE_REASON.vatMismatch);
  // A foreign receipt matched to a bank row took the SEK amount from the row:
  // still a flag, so the user glances at it ("ready" means no flags).
  if (foreign && matched && amount !== null) soft.push(TRIAGE_REASON.foreignCurrencyMatched(reading.currency.toUpperCase(), amount));
  for (const flag of flags) {
    if (SOFT_FLAGS.has(flag)) soft.push(TRIAGE_REASON.softFlag[flag] ?? 'Kolla en sak.');
  }
  if (proposal.question) soft.push(TRIAGE_REASON.question);
  // Memory and the receipt disagree. Neither is authoritative on its own, so
  // the card asks instead of quietly picking the one that has been right most
  // often — that is precisely the purchase where it has not been.
  if (input.categoryConflict) soft.push(TRIAGE_REASON.categoryConflict(input.categoryConflict.remembered, input.categoryConflict.read));
  if (aiSuggested && !matched && proposal.confidence >= CHECK_CONFIDENCE && proposal.confidence < READY_CONFIDENCE) {
    soft.push(TRIAGE_REASON.mediumConfidence);
  }
  if (amount !== null && amount > settings.batchThresholdOre && amount <= ALWAYS_NEEDS_YOU_ORE) {
    soft.push(TRIAGE_REASON.aboveBatch(settings.batchThresholdOre));
  }

  if (hard.length) return { tier: 'needs_you', reasons: unique(hard), flags };
  if (soft.length) return { tier: 'check', reasons: unique(soft), flags };

  // ---- ready: nothing to check. Provenance ("från minnet", "matchad mot …")
  // is added by the pipeline, which knows the rule and the bank row.
  return { tier: 'ready', reasons: [], flags };
}

function needsCategory(kind: TriageInput['proposal']['kind']): boolean {
  switch (kind) {
    case 'purchase':
    case 'sale':
    case 'refund':
      return true;
    case 'transfer':
    case 'owner_in':
    case 'owner_out':
    case 'tax':
    case 'settle':
    case 'vat_settlement':
    case 'year_end':
    case 'other':
      return false;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown kind ${String(exhaustive)}`);
    }
  }
}

function unique(list: string[]): string[] {
  return [...new Set(list)];
}

/** Whether an item may be part of "[Bokför N klara]": ready and within the batch threshold. */
export function batchEligible(item: { tier?: InboxTier | undefined; status: string; proposal?: { amountOre: Ore | null } | undefined }, batchThresholdOre: Ore): boolean {
  if (item.status !== 'ready' || item.tier !== 'ready') return false;
  const amount = item.proposal?.amountOre ?? null;
  return amount !== null && amount > 0 && amount <= batchThresholdOre;
}
